export function formatSiteLocalTime(
  timestamp: string | null | undefined,
  timezone: string,
  locale = "en-US",
  fallback = "unknown"
): string {
  if (!timestamp) {
    return fallback;
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short"
  }).format(date);
}

export function formatForecastDay(day: string, locale = "en-US"): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return day;
  const value = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  }).format(value);
}

export function formatCycleLabel(
  timestamp: string | null | undefined,
  timezone: string,
  locale = "en-US"
): string {
  if (!timestamp) {
    return "unknown";
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return "unknown";
  }
  const zHour = date.toISOString().slice(11, 13);
  return `${zHour}Z (${formatSiteLocalTime(timestamp, timezone, locale)})`;
}

export function formatAgeLabel(seconds: number | null | undefined, locale = "en-US"): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return "unknown age";
  }
  const hours = seconds / 3600;
  if (hours >= 24) {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(hours)} h`;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(hours)} h`;
}

type ForecastAgeClock = {
  fetched_at: string | null | undefined;
  checked_at: string | null | undefined;
  fetch_age_seconds: number | null | undefined;
};

function parseTimestampMs(timestamp: string | null | undefined): number | null {
  if (!timestamp) {
    return null;
  }
  const parsed = new Date(timestamp).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Advance the server-reported age against its own check timestamp. This keeps
 * a cached response honest: receiving the same body again does not reset its
 * age to the value frozen when that body was serialized.
 */
export function currentForecastAgeSeconds(
  freshness: ForecastAgeClock,
  nowMs: number = Date.now()
): number | null {
  const checkedAtMs = parseTimestampMs(freshness.checked_at);
  if (
    freshness.fetch_age_seconds !== null &&
    freshness.fetch_age_seconds !== undefined &&
    Number.isFinite(freshness.fetch_age_seconds) &&
    checkedAtMs !== null
  ) {
    const elapsedSinceCheckSeconds = Math.max(0, (nowMs - checkedAtMs) / 1000);
    return Math.max(0, freshness.fetch_age_seconds + elapsedSinceCheckSeconds);
  }

  const fetchedAtMs = parseTimestampMs(freshness.fetched_at);
  if (fetchedAtMs === null || !Number.isFinite(nowMs)) {
    return null;
  }
  return Math.max(0, (nowMs - fetchedAtMs) / 1000);
}
